/**
 * Owner-only Reporting & Service-Management page.
 *
 * Phase 1 of the 19-item governance-and-reporting backlog. Six dashboards
 * backed by data the platform already collects:
 *   #1 Tenant Health — single-screen churn-risk roll-up
 *   #2 Per-tenant Usage — DAU/WAU/MAU + top routes (drill-down)
 *   #4 Pilot Scorecard — per-pilot KPI status
 *   #5 Revenue MRR/ARR — removed (fixed-pricing model; no billing integration)
 *   #6 Pipeline Funnel — SRL → demo → pilot → conversion drop-off
 *  #15 What's New — last 5 platform releases
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import {
  HeartPulse, BarChart3, Filter, Sparkles, ClipboardList, Loader2,
} from "lucide-react";

const BAND_BG = {
  red: "bg-rose-100 text-rose-800",
  amber: "bg-amber-100 text-amber-800",
  green: "bg-emerald-100 text-emerald-800",
  unscored: "bg-slate-100 text-slate-700",
};

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section
    className="rounded-2xl border border-black/5 bg-white p-6"
    data-testid={testid}
  >
    <header className="mb-4 flex items-start gap-3">
      <div className="rounded-xl bg-ek-purple/10 p-2 text-ek-purple">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <h2 className="font-display text-lg font-semibold text-ek-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-ek-ink/60">{subtitle}</p>}
      </div>
    </header>
    {children}
  </section>
);

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-black/5">
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/50">{label}</p>
    <p className="mt-0.5 text-lg font-semibold text-ek-ink">{value}</p>
  </div>
);

function TenantHealthSection() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openTenant, setOpenTenant] = useState(null);
  const [usage, setUsage] = useState(null);
  const [nudging, setNudging] = useState(null);

  useEffect(() => {
    api.get("/owner/tenant-health")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const openUsage = async (tenant_id) => {
    setOpenTenant(tenant_id);
    setUsage(null);
    try {
      const r = await api.get(`/owner/tenant-health/${tenant_id}/usage`);
      setUsage(r.data);
    } catch (e) {
      toast.error("Usage load failed");
    }
  };

  const reEngage = async (tenant_id) => {
    if (!confirm(`Send a re-engagement email to ${tenant_id}'s org-admin?`)) return;
    setNudging(tenant_id);
    try {
      const r = await api.post(`/owner/tenant-health/${tenant_id}/re-engage`);
      if (r.data.sent) {
        toast.success(`Re-engagement email sent to ${r.data.recipient_email}.`);
      } else {
        toast.error(r.data.error || "Send failed");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setNudging(null);
    }
  };

  return (
    <Section
      icon={HeartPulse}
      title="Tenant Health"
      subtitle="Single-screen churn-risk view across every tenant. Red = stale; amber = at risk; green = engaged."
      testid="reporting-tenant-health"
    >
      {err && <p className="text-xs text-rose-700">{err}</p>}
      {!data && !err && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-ek-ink/50">
              <tr>
                <th className="py-2 pr-4">Tenant</th>
                <th className="py-2 pr-4">Band</th>
                <th className="py-2 pr-4">Users</th>
                <th className="py-2 pr-4">Activation</th>
                <th className="py-2 pr-4">Last login</th>
                <th className="py-2 pr-4">Last sync</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.tenant_id} className="border-b border-black/5">
                  <td className="py-2 pr-4 font-medium text-ek-ink">
                    {r.tenant_name}
                    <span className="ml-2 font-mono text-[10px] text-ek-ink/40">{r.tenant_id}</span>
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BAND_BG[r.band]}`}
                      data-testid={`tenant-band-${r.tenant_id}`}
                    >
                      {r.band}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{r.user_count}</td>
                  <td className="py-2 pr-4">{r.activation_pct == null ? "—" : `${r.activation_pct}%`}</td>
                  <td className="py-2 pr-4">{r.days_since_login == null ? "—" : `${r.days_since_login}d ago`}</td>
                  <td className="py-2 pr-4">{r.days_since_sync == null ? "—" : `${r.days_since_sync}d ago`}</td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openUsage(r.tenant_id)}
                        className="text-xs font-semibold text-ek-purple hover:underline"
                        data-testid={`tenant-usage-btn-${r.tenant_id}`}
                      >
                        Usage →
                      </button>
                      {(r.band === "red" || r.band === "amber") && (
                        <button
                          onClick={() => reEngage(r.tenant_id)}
                          disabled={nudging === r.tenant_id}
                          className="rounded-md bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
                          data-testid={`tenant-reengage-btn-${r.tenant_id}`}
                          title="Send a re-engagement email to this tenant's org-admin"
                        >
                          {nudging === r.tenant_id ? "Sending…" : "Nudge"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openTenant && (
        <div className="mt-4 rounded-lg border border-ek-purple/20 bg-ek-purple/5 p-4" data-testid="tenant-usage-drilldown">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ek-ink">
              Usage for <span className="font-mono text-xs">{openTenant}</span>
            </h3>
            <button
              onClick={() => setOpenTenant(null)}
              className="text-xs text-ek-ink/50 hover:text-ek-ink"
            >
              Close
            </button>
          </div>
          {!usage && <Loader2 className="mt-2 h-4 w-4 animate-spin text-ek-ink/40" />}
          {usage && (
            <>
              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <Metric label="DAU" value={usage.dau} />
                <Metric label="WAU" value={usage.wau} />
                <Metric label="MAU" value={usage.mau} />
              </div>
              <p className="mt-3 text-xs text-ek-ink/60">
                Top routes (last 30 days, {usage.events_30d} events)
              </p>
              <ul className="mt-1 space-y-1 text-xs">
                {usage.top_routes.length === 0 && <li className="text-ek-ink/40">No events recorded.</li>}
                {usage.top_routes.map((r) => (
                  <li key={r.route} className="flex justify-between font-mono">
                    <span className="truncate text-ek-ink/70">{r.route}</span>
                    <span className="text-ek-ink/50">{r.count}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

function RevenueSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/owner/revenue").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const sym = data?.currency === "usd" ? "$" : "";
  return (
    <Section
      icon={BarChart3}
      title="Revenue · MRR / ARR"
      subtitle="Aggregated from paid transactions. One-time pilot fees surfaced separately (rolling 90 days)."
      testid="reporting-revenue"
    >
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label={`MRR · ${data.currency?.toUpperCase()}`} value={`${sym}${data.mrr.toLocaleString()}`} />
            <Metric label={`ARR · ${data.currency?.toUpperCase()}`} value={`${sym}${data.arr.toLocaleString()}`} />
            <Metric label="Active subs" value={data.subscription_count} />
          </div>
          <p className="mt-4 text-xs text-ek-ink/60">By plan</p>
          <table className="mt-1 min-w-full text-sm" data-testid="revenue-by-plan">
            <thead className="text-xs uppercase tracking-wide text-ek-ink/50">
              <tr><th className="py-1 text-left">Plan</th><th className="py-1 text-right">Count</th><th className="py-1 text-right">Gross</th></tr>
            </thead>
            <tbody>
              {data.by_plan.length === 0 && (
                <tr><td colSpan="3" className="py-2 text-ek-ink/40">No paid transactions yet.</td></tr>
              )}
              {data.by_plan.map((p) => (
                <tr key={p.package_id} className="border-t border-black/5">
                  <td className="py-1.5 pr-4">
                    {p.name}<span className="ml-2 text-[10px] text-ek-ink/40">{p.kind}</span>
                  </td>
                  <td className="py-1.5 text-right">{p.count}</td>
                  <td className="py-1.5 text-right font-mono">{sym}{p.gross.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-ek-ink/60">
            Rolling 90-day one-time pilot revenue:{" "}
            <span className="font-mono font-semibold text-ek-ink">{sym}{data.pilot_revenue_90d.toLocaleString()}</span>
          </p>
        </>
      )}
    </Section>
  );
}

function FunnelSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owner/funnel").then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <Section
      icon={Filter}
      title="Pipeline Funnel"
      subtitle="Where the funnel is leaking. Conversion % is stage-over-stage."
      testid="reporting-funnel"
    >
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <ol className="space-y-2">
          {data.stages.map((s, i) => (
            <li key={s.id} className="flex items-center gap-3" data-testid={`funnel-stage-${s.id}`}>
              <span className="w-6 text-center font-mono text-xs text-ek-ink/40">{i + 1}</span>
              <span className="min-w-[220px] flex-1 text-sm text-ek-ink">{s.name}</span>
              <span className="font-mono text-base font-semibold text-ek-ink">{s.count}</span>
              {s.conversion_pct != null && (
                <span className={`min-w-[64px] rounded-full px-2 py-0.5 text-[11px] font-semibold text-right ${
                  s.conversion_pct >= 30 ? "bg-emerald-100 text-emerald-800"
                  : s.conversion_pct >= 10 ? "bg-amber-100 text-amber-800"
                  : "bg-rose-100 text-rose-800"
                }`}>{s.conversion_pct}%</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

function WhatsNewSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owner/whats-new?limit=5").then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <Section
      icon={Sparkles}
      title="What's New"
      subtitle="Latest 5 platform releases — pulled from CHANGELOG."
      testid="reporting-whats-new"
    >
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <ul className="space-y-3" data-testid="whats-new-list">
          {data.items.length === 0 && <li className="text-xs text-ek-ink/40">No entries.</li>}
          {data.items.map((item) => (
            <li key={item.slug} className="border-l-2 border-ek-purple/30 pl-3">
              <h3 className="text-sm font-semibold text-ek-ink">{item.title}</h3>
              {item.summary && <p className="mt-0.5 text-xs text-ek-ink/60">{item.summary}</p>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PilotScorecardSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/owner/pilot-scorecard").then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <Section
      icon={ClipboardList}
      title="Pilot Scorecard"
      subtitle="Per-pilot KPI status against the agreed kick-off targets. Sets the renewal conversation."
      testid="reporting-pilot-scorecard"
    >
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && data.items.length === 0 && (
        <p className="text-xs text-ek-ink/50">
          No pilot bookings yet. Once a pilot lands in <code>pilot_bookings</code>,
          set its <code>kpis</code> array to start scoring.
        </p>
      )}
      {data && data.items.length > 0 && (
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-ek-ink/50 text-left">
            <tr><th className="py-1">Pilot</th><th className="py-1">Tenant</th><th className="py-1">Band</th><th className="py-1">KPIs</th></tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.pilot_id} className="border-t border-black/5">
                <td className="py-1.5 font-mono text-xs">{p.pilot_id}</td>
                <td className="py-1.5">{p.tenant_name || p.tenant_id}</td>
                <td className="py-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BAND_BG[p.overall_band]}`}>
                    {p.overall_band}
                  </span>
                </td>
                <td className="py-1.5 text-xs text-ek-ink/60">
                  {p.kpis.length} KPI{p.kpis.length === 1 ? "" : "s"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export default function OwnerReporting() {
  return (
    <>
      <Seo title="Reporting · ERIO" />
      <Navbar />
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8" data-testid="owner-reporting-page">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-ek-purple font-semibold">
            EKSELENS · ERIO · OWNER
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink">
            Reporting & Service Management
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
            Phase 1 of the governance-and-reporting backlog. Tenant health,
            revenue, funnel conversion, pilot scorecard, and release history —
            all computed live from the existing collections.
          </p>
        </header>
        <TenantHealthSection />
        {/* RevenueSection removed — ERIO uses a fixed pricing model (no Stripe). */}
        <FunnelSection />
        <PilotScorecardSection />
        <WhatsNewSection />
      </main>
    </>
  );
}
